/**
 * `live-shaped` profile rules — the numbers, and nothing else (plan §1.2 · §4.7).
 *
 * Split out of `adapters/local/live-shaped.ts` so this module imports NOTHING.
 * Two consumers need these constants and only one of them can resolve a
 * package graph: `scripts/extract-demo-fixtures-lib.ts` runs under
 * `node --experimental-strip-types`, which does not resolve the extensionless
 * relative specifiers `@meridian/contracts`' barrel is written with. Importing
 * the adapter — whose first statement is a value import of `contracts` —
 * therefore broke `pnpm fixtures:extract` with ERR_MODULE_NOT_FOUND, and no
 * gate caught it: tsc resolves extensionless fine, ESLint disables the rule
 * under `scripts/**`, dependency-cruiser's `includeOnly` excludes `scripts/`,
 * and no workflow invokes the script.
 *
 * Keeping the rules here rather than duplicating them is the point: the
 * runtime adapter and the committed fixtures must not be able to describe two
 * different "live-shaped". `adapters/local/live-shaped.ts` re-exports every
 * symbol below, so existing importers are unaffected.
 */

/** Closed profile vocabulary; `apps/web` validates `VITE_SYNTHETIC_PROFILE` against it. */
export const SYNTHETIC_PROFILES = Object.freeze(['demo-fidelity', 'live-shaped'] as const);
export type SyntheticProfile = (typeof SYNTHETIC_PROFILES)[number];

/**
 * Every 17th row loses its P/E, every 23rd its dividend, and row 0 stands in for
 * a recent listing with no usable history — PLUS any row whose demo value is
 * already 0, which both consumers apply with `||` (see the note beside that
 * disjunction in `adapters/local/live-shaped.ts`). Coprime moduli so the two
 * null sets overlap on exactly one row in 391 rather than on every shared
 * multiple.
 */
export const LIVE_SHAPE_NULL_PE_MODULUS = 17;
export const LIVE_SHAPE_NULL_DIV_MODULUS = 23;
export const LIVE_SHAPE_INSUFFICIENT_HISTORY_INDEX = 0;

/**
 * ADR-0034's three nullable additions. Live v1 seeds successfully with no
 * `ratios.csv` / `income_annual.csv`, so `financials: null` is not an edge case
 * — it is the DOMINANT production inhabitant. Leaving the synthetic object in
 * place made that empty state unreachable in local mode, which is the reason
 * `apps/api/test/parity.test.ts` had to wrap its ratio-key comparison in a
 * truthiness guard that then never executed.
 *
 * Coprime with 17, 23 and the 29 of LIVE_SHAPE_NULL_METRICS_MODULUS, and with
 * each other, for the same reason the two above are: overlapping null sets on
 * every shared multiple would collapse the separately-countable shapes into one.
 * 29 specifically is NOT available here — it is already the noMetrics modulus,
 * so reusing it would make "no financials" and "no metrics" the same row set.
 */
export const LIVE_SHAPE_NULL_FINANCIALS_MODULUS = 41;
export const LIVE_SHAPE_NULL_SHARES_OUT_MODULUS = 31;
export const LIVE_SHAPE_NULL_AVG_VOLUME_MODULUS = 37;
/**
 * ADR-0035 day-one inhabitant: ratios/income populated, balance-sheet and
 * cash-flow empty-honest until the new CSVs are seeded. Coprime with 41 so
 * "no financials at all" and "statements empty" stay separately countable.
 */
export const LIVE_SHAPE_EMPTY_STATEMENTS_MODULUS = 43;

export interface OutOfClampSample {
  readonly a: string;
  readonly b: string;
  readonly rho: number;
}

/**
 * Pairs whose ρ is forced outside the synthetic [-0.85, 0.95] clamp band.
 *
 * Named pairs rather than positional ones, so the value a reader sees in the
 * committed fixtures is the value the runtime adapter emits. A pair whose
 * tickers are absent from the row set is skipped: an injected fixture of one
 * sector is a legitimate row set, and throwing there would make the profile
 * unusable for exactly the small sets tests are built from.
 */
export const OUT_OF_CLAMP_SAMPLES: readonly OutOfClampSample[] = Object.freeze([
  Object.freeze({ a: 'AAPL', b: 'MSFT', rho: 0.98 }),
  Object.freeze({ a: 'XOM', b: 'TSLA', rho: -0.91 }),
]);
